import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, X } from 'lucide-react';

/**
 * Barra de búsqueda y filtros para los listados.
 *
 * Se comparte entre Consultas y Pacientes para que filtrar se sienta igual en
 * todo el sistema: mismo sitio, mismo orden, misma forma de limpiar.
 *
 * Declarativa: la página describe qué filtros quiere y esta barra los pinta.
 *
 * @param {string} search - Texto de búsqueda
 * @param {Function} onSearchChange
 * @param {string} searchPlaceholder
 * @param {Array} filters - [{key, label, value, options:[{value,label}], onChange}]
 * @param {Object} dateRange - {desde, hasta} o null si el listado no filtra por fecha
 * @param {Function} onDateChange - (campo, valor)
 * @param {number} resultCount - Cuántos elementos quedan tras filtrar
 * @param {number} totalCount - Cuántos había en total
 * @param {Function} onClear
 */
export default function FilterBar({
  search, onSearchChange, searchPlaceholder = 'Buscar...',
  filters = [], dateRange = null, onDateChange,
  resultCount, totalCount, onClear,
}) {
  const hasActiveFilters =
    Boolean(search) ||
    filters.some(f => f.value && f.value !== 'todos') ||
    Boolean(dateRange?.desde) ||
    Boolean(dateRange?.hasta);

  return (
    <div className="space-y-3">
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9"
            aria-label={searchPlaceholder}
          />
        </div>

        {filters.map(({ key, label, value, options, onChange }) => (
          <Select key={key} value={value || 'todos'} onValueChange={onChange}>
            <SelectTrigger className="w-full lg:w-[180px]" aria-label={label}>
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{label}: todos</SelectItem>
              {options.map(option => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

        {dateRange && (
          <div className="flex items-center gap-2">
            <Input type="date" value={dateRange.desde || ''} max={dateRange.hasta || undefined}
                   onChange={(e) => onDateChange('desde', e.target.value)}
                   className="w-auto" aria-label="Desde" />
            <span className="text-slate-400 text-sm">a</span>
            <Input type="date" value={dateRange.hasta || ''} min={dateRange.desde || undefined}
                   onChange={(e) => onDateChange('hasta', e.target.value)}
                   className="w-auto" aria-label="Hasta" />
          </div>
        )}

        {hasActiveFilters && (
          <Button variant="ghost" onClick={onClear} className="shrink-0">
            <X className="w-4 h-4 mr-1" /> Limpiar
          </Button>
        )}
      </div>

      {/* Sin esto, un filtro que no deja nada parece un error de carga */}
      {hasActiveFilters && (
        <p className="text-xs text-slate-500">
          {resultCount === 0
            ? 'Ningún resultado con estos filtros'
            : `${resultCount} de ${totalCount} ${resultCount === 1 ? 'resultado' : 'resultados'}`}
        </p>
      )}
    </div>
  );
}
